import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Money } from '../ui/Money';
import { Plane, TrendingUp, TrendingDown } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// Shared editor for one leave-driven company-paid ticket record. Filling the
// Actual Ticket Issue Date marks it "issued" and removes it from the dashboard
// reminder. Used from both the Dashboard reminder card and the employee profile.
export const TicketRecordDialog = ({ record, open, onOpenChange, onSaved }) => {
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const token = localStorage.getItem('token');

  useEffect(() => {
    if (record) {
      setForm({
        issue_due_date: record.issue_due_date || '',
        actual_issue_date: record.actual_issue_date || '',
        approved_fare: record.approved_fare ?? '',
        actual_fare: record.actual_fare ?? '',
        remarks: record.remarks || '',
      });
    }
  }, [record]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const approved = form.approved_fare === '' ? null : Number(form.approved_fare);
  const actual = form.actual_fare === '' ? null : Number(form.actual_fare);
  const diff = approved != null && actual != null ? approved - actual : null;

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/api/ticket-tracker/record/${record.id}`,
        {
          issue_due_date: form.issue_due_date || null,
          actual_issue_date: form.actual_issue_date || null,
          approved_fare: form.approved_fare === '' ? null : Number(form.approved_fare),
          actual_fare: form.actual_fare === '' ? null : Number(form.actual_fare),
          remarks: form.remarks || null,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast({ title: 'Saved', description: form.actual_issue_date ? 'Ticket marked as issued.' : 'Ticket details updated.' });
      onOpenChange(false);
      onSaved && onSaved(res.data);
    } catch (err) {
      toast({ title: 'Error', description: err?.response?.data?.detail || 'Could not save ticket', variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (!record) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="ticket-record-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plane className="h-5 w-5 text-sky-600" />
            Ticket Details — {record.employee_name}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3">
            <span className="font-mono">{record.employee_code}</span> · {record.department || '—'}
            <span className="mx-2">|</span>
            Leave starts <span className="font-medium text-gray-700">{record.leave_start_date}</span>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Date of Flying</Label>
              <Input type="date" value={form.issue_due_date} onChange={(e) => set('issue_due_date', e.target.value)} data-testid="ticket-issue-due-date" />
            </div>
            <div>
              <Label className="text-xs">Ticket Issue Date</Label>
              <Input type="date" value={form.actual_issue_date} onChange={(e) => set('actual_issue_date', e.target.value)} data-testid="ticket-actual-issue-date" />
            </div>
            <div>
              <Label className="text-xs">Company Approved Fare (AED)</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.approved_fare} onChange={(e) => set('approved_fare', e.target.value)} data-testid="ticket-approved-fare" />
            </div>
            <div>
              <Label className="text-xs">Actual Ticket Fare (AED)</Label>
              <Input type="number" min="0" step="0.01" placeholder="0.00" value={form.actual_fare} onChange={(e) => set('actual_fare', e.target.value)} data-testid="ticket-actual-fare" />
            </div>
          </div>

          {diff != null && (
            <div
              className={`flex items-center justify-between rounded-lg px-3 py-2 text-sm font-semibold ${
                diff > 0 ? 'bg-emerald-50 text-emerald-700' : diff < 0 ? 'bg-rose-50 text-rose-700' : 'bg-gray-50 text-gray-600'
              }`}
              data-testid="ticket-fare-comparison"
            >
              <span className="flex items-center gap-1">
                {diff > 0 ? <TrendingDown className="h-4 w-4" /> : diff < 0 ? <TrendingUp className="h-4 w-4" /> : null}
                {diff > 0 ? 'Saved vs budget' : diff < 0 ? 'Over budget' : 'On budget'}
              </span>
              <span className="inline-flex items-center gap-1">
                {diff > 0 ? '+' : diff < 0 ? '-' : ''}<Money value={Math.abs(diff)} digits={2} />
              </span>
            </div>
          )}

          <div>
            <Label className="text-xs">Remarks / Comments</Label>
            <Textarea rows={2} value={form.remarks} onChange={(e) => set('remarks', e.target.value)} placeholder="Optional notes…" data-testid="ticket-remarks" />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="ticket-record-cancel">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="ticket-record-save">
            {saving ? 'Saving…' : form.actual_issue_date ? 'Save & Mark Issued' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default TicketRecordDialog;
